import type { AutomationCurve, AutomationLane, AutomationPoint, AutomationTarget, Project } from '@/domain/types';
import { withTrack } from '@/domain/mutate';
import { resolveParamDescriptor } from '@/engine/automation';
import { normaliseParam } from '@/engine/effects/descriptors';
import { thinPoints } from '@/domain/curveThin';
import { newLaneId, newPointId } from '@/util/id';
import { defineCommand } from './registry';
import { boolParam, enumParam, idParam, idsParam, intParam, numberParam, omitted, stringParam } from './params';

/**
 * Automation editing.
 *
 * Lanes live in `project.automation` and are referenced by their track, the
 * same shape clips use. Points are always kept sorted by position, so every
 * reader — evaluation, drawing, hit testing — can rely on it without sorting
 * defensively on a hot path.
 *
 * A new lane starts with a single point holding the parameter's *current*
 * value. An empty lane would otherwise change nothing until the user drew a
 * second point, and a lane that snapped a parameter to a default the moment it
 * was created would be worse still.
 */

const CURVES: readonly AutomationCurve[] = ['linear', 'hold', 'smooth'];

/** Two points closer than this are treated as the same position. */
const MERGE_TOLERANCE_TICKS = 2;

function withLane(project: Project, laneId: string, update: (lane: AutomationLane) => AutomationLane): Project {
  const existing = project.automation[laneId];
  if (!existing) return project;
  const next = update(existing);
  if (next === existing) return project;
  return { ...project, automation: { ...project.automation, [laneId]: next } };
}

function sortPoints(points: AutomationPoint[]): AutomationPoint[] {
  return [...points].sort((a, b) => a.tick - b.tick || a.id.localeCompare(b.id));
}

/** The parameter's current static value, normalised, for seeding a new lane. */
function currentNormalisedValue(project: Project, target: AutomationTarget): number {
  const descriptor = resolveParamDescriptor(project, target);
  if (!descriptor) return 0.5;
  const track = project.tracks[target.trackId];
  if (!track) return normaliseParam(descriptor, descriptor.default);

  if (target.kind === 'track') {
    const value = target.paramId === 'volume' ? track.volume : target.paramId === 'pan' ? track.pan : descriptor.default;
    return normaliseParam(descriptor, value);
  }
  if (target.kind === 'instrument') {
    return normaliseParam(descriptor, track.instrument?.params[target.paramId] ?? descriptor.default);
  }
  const effect = track.effects.find((e) => e.id === target.effectId);
  return normaliseParam(descriptor, effect?.params[target.paramId] ?? descriptor.default);
}

/**
 * Replaces everything in a stretch of a lane with the points just performed.
 *
 * This is what recording a fader move commits, and it is one command rather than
 * a stream of them for three reasons: the whole pass is a single undo step; the
 * points that were there before are *replaced* rather than merged with, which is
 * what "I just performed this bit again" means; and a gesture that emits three
 * hundred point-adds would fill the history with noise.
 *
 * Points arriving outside the region are dropped rather than trusted: the region
 * is the claim about what was performed, and a point beyond it would silently
 * overwrite automation the performer never touched.
 */
export const writeAutomationRegion = defineCommand<{
  laneId: string;
  startTicks: number;
  endTicks: number;
  points: { tick: number; value: number }[];
}>({
  type: 'automation.writeRegion',
  label: () => 'Record automation',
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    const startTicks = intParam(raw.startTicks, 0, Number.MAX_SAFE_INTEGER);
    const endTicks = intParam(raw.endTicks, 0, Number.MAX_SAFE_INTEGER);
    if (!laneId || startTicks === null || endTicks === null) return null;
    if (!Array.isArray(raw.points)) return null;

    const points: { tick: number; value: number }[] = [];
    for (const entry of raw.points) {
      if (typeof entry !== 'object' || entry === null) return null;
      const record = entry as Record<string, unknown>;
      const tick = intParam(record.tick, 0, Number.MAX_SAFE_INTEGER);
      const value = numberParam(record.value, 0, 1);
      if (tick === null || value === null) return null;
      points.push({ tick, value });
    }
    if (points.length > 20000) return null;
    return { laneId, startTicks, endTicks, points };
  },
  apply: (project, p) => {
    const lane = project.automation[p.laneId];
    if (!lane) return project;
    const from = Math.min(p.startTicks, p.endTicks);
    const to = Math.max(p.startTicks, p.endTicks);

    const written = p.points
      .filter((point) => point.tick >= from && point.tick <= to)
      .map((point) => ({
        id: newPointId(),
        tick: point.tick,
        value: point.value,
        curve: 'linear' as AutomationCurve,
      }));
    if (written.length === 0) return project;

    const kept = lane.points.filter((point) => point.tick < from || point.tick > to);
    return withLane(project, p.laneId, (existing) => ({
      ...existing,
      points: sortPoints([...kept, ...written]),
    }));
  },
});

export const addAutomationLane = defineCommand<{
  laneId: string;
  trackId: string;
  kind: AutomationTarget['kind'];
  effectId: string | null;
  paramId: string;
}>({
  type: 'automation.addLane',
  label: () => 'Add automation lane',
  parse: (raw) => {
    const laneId = raw.laneId === undefined ? newLaneId() : idParam(raw.laneId);
    const trackId = idParam(raw.trackId);
    const kind = enumParam(raw.kind, ['track', 'effect', 'instrument'] as const, 'track');
    const paramId = stringParam(raw.paramId, 64);
    if (!laneId || !trackId || !kind || !paramId) return null;
    const effectId = raw.effectId === undefined || raw.effectId === null ? null : idParam(raw.effectId);
    if (kind === 'effect' && !effectId) return null;
    return { laneId, trackId, kind, effectId, paramId };
  },
  apply: (project, p) => {
    if (project.automation[p.laneId]) return project;
    const target: AutomationTarget = {
      kind: p.kind,
      trackId: p.trackId,
      effectId: p.effectId,
      paramId: p.paramId,
    };
    // Refuse lanes for parameters that cannot be ramped, rather than creating a
    // control that silently does nothing.
    if (!resolveParamDescriptor(project, target)) return project;

    const track = project.tracks[p.trackId];
    if (!track) return project;
    const duplicate = track.automationLaneIds.some((id) => {
      const lane = project.automation[id];
      return (
        lane &&
        lane.target.kind === target.kind &&
        lane.target.paramId === target.paramId &&
        lane.target.effectId === target.effectId
      );
    });
    if (duplicate) return project;

    const lane: AutomationLane = {
      id: p.laneId,
      target,
      enabled: true,
      height: 62,
      points: [
        {
          id: newPointId(),
          tick: 0,
          value: currentNormalisedValue(project, target),
          curve: 'linear',
        },
      ],
    };

    return withTrack({ ...project, automation: { ...project.automation, [lane.id]: lane } }, p.trackId, (t) => ({
      ...t,
      automationLaneIds: [...t.automationLaneIds, lane.id],
      showAutomation: true,
    }));
  },
});

export const removeAutomationLane = defineCommand<{ laneId: string }>({
  type: 'automation.removeLane',
  label: () => 'Remove automation lane',
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    return laneId ? { laneId } : null;
  },
  apply: (project, p) => {
    const lane = project.automation[p.laneId];
    if (!lane) return project;
    const automation = { ...project.automation };
    delete automation[p.laneId];
    return withTrack({ ...project, automation }, lane.target.trackId, (t) => ({
      ...t,
      automationLaneIds: t.automationLaneIds.filter((id) => id !== p.laneId),
    }));
  },
});

export const setLaneEnabled = defineCommand<{ laneId: string; enabled: boolean }>({
  type: 'automation.setLaneEnabled',
  label: (p) => (p.enabled ? 'Enable automation lane' : 'Disable automation lane'),
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    const enabled = boolParam(raw.enabled);
    return laneId && enabled !== null ? { laneId, enabled } : null;
  },
  apply: (project, p) => withLane(project, p.laneId, (lane) => ({ ...lane, enabled: p.enabled })),
});

export const setLaneHeight = defineCommand<{ laneId: string; height: number }>({
  type: 'automation.setLaneHeight',
  label: () => 'Resize automation lane',
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    const height = intParam(raw.height, 28, 240);
    return laneId && height !== null ? { laneId, height } : null;
  },
  coalesceKey: (p) => `laneHeight:${p.laneId}`,
  apply: (project, p) => withLane(project, p.laneId, (lane) => ({ ...lane, height: p.height })),
});

export const showTrackAutomation = defineCommand<{ trackId: string; show: boolean }>({
  type: 'automation.setVisible',
  label: (p) => (p.show ? 'Show automation' : 'Hide automation'),
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const show = boolParam(raw.show);
    return trackId && show !== null ? { trackId, show } : null;
  },
  apply: (project, p) => withTrack(project, p.trackId, (t) => ({ ...t, showAutomation: p.show })),
});

export const addAutomationPoint = defineCommand<{
  laneId: string;
  pointId: string;
  tick: number;
  value: number;
  curve: AutomationCurve;
}>({
  type: 'automation.addPoint',
  label: () => 'Add automation point',
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    const pointId = raw.pointId === undefined ? newPointId() : idParam(raw.pointId);
    const tick = intParam(raw.tick, 0, Number.MAX_SAFE_INTEGER);
    const value = numberParam(raw.value, 0, 1);
    if (!laneId || !pointId || tick === null || value === null) return null;
    return { laneId, pointId, tick, value, curve: enumParam(raw.curve, CURVES, 'linear') ?? 'linear' };
  },
  apply: (project, p) =>
    withLane(project, p.laneId, (lane) => {
      // Clicking on top of an existing point moves it rather than stacking a
      // second one at the same position, which would be invisible and undeletable.
      const existing = lane.points.find((point) => Math.abs(point.tick - p.tick) <= MERGE_TOLERANCE_TICKS);
      if (existing) {
        return {
          ...lane,
          points: lane.points.map((point) =>
            point.id === existing.id ? { ...point, value: p.value, curve: p.curve } : point,
          ),
        };
      }
      const point: AutomationPoint = { id: p.pointId, tick: p.tick, value: p.value, curve: p.curve };
      return { ...lane, points: sortPoints([...lane.points, point]) };
    }),
});

export const moveAutomationPoint = defineCommand<{ laneId: string; pointId: string; tick: number; value: number }>({
  type: 'automation.movePoint',
  label: () => 'Move automation point',
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    const pointId = idParam(raw.pointId);
    const tick = intParam(raw.tick, 0, Number.MAX_SAFE_INTEGER);
    const value = numberParam(raw.value, 0, 1);
    return laneId && pointId && tick !== null && value !== null ? { laneId, pointId, tick, value } : null;
  },
  coalesceKey: (p) => `autoPoint:${p.laneId}:${p.pointId}`,
  apply: (project, p) =>
    withLane(project, p.laneId, (lane) => {
      if (!lane.points.some((point) => point.id === p.pointId)) return lane;
      const points = lane.points.map((point) =>
        point.id === p.pointId ? { ...point, tick: p.tick, value: p.value } : point,
      );
      return { ...lane, points: sortPoints(points) };
    }),
});

/**
 * Moves a set of points together, in time and in value.
 *
 * One command for the whole selection rather than one per point: nudging fifty
 * points should be one undo step, and dragging them should coalesce into one as
 * well.
 */
export const moveAutomationPoints = defineCommand<{
  laneId: string;
  pointIds: string[];
  deltaTicks: number;
  deltaValue: number;
}>({
  type: 'automation.movePoints',
  label: (p) => `Move ${p.pointIds.length} automation points`,
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    const pointIds = idsParam(raw.pointIds);
    const deltaTicks = intParam(raw.deltaTicks, -Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 0);
    const deltaValue = numberParam(raw.deltaValue, -1, 1, 0);
    if (!laneId || !pointIds || pointIds.length === 0 || deltaTicks === null || deltaValue === null) return null;
    return { laneId, pointIds, deltaTicks, deltaValue };
  },
  coalesceKey: (p) => `autoPoints:${p.laneId}:${p.pointIds.join(',')}`,
  apply: (project, p) => {
    if (p.deltaTicks === 0 && p.deltaValue === 0) return project;
    const chosen = new Set(p.pointIds);
    return withLane(project, p.laneId, (lane) => {
      if (!lane.points.some((point) => chosen.has(point.id))) return lane;
      const points = lane.points.map((point) =>
        chosen.has(point.id)
          ? {
              ...point,
              tick: Math.max(0, point.tick + p.deltaTicks),
              value: Math.min(1, Math.max(0, point.value + p.deltaValue)),
            }
          : point,
      );
      return { ...lane, points: sortPoints(points) };
    });
  },
});

/**
 * Scales a selection about a pivot: the move made bigger or smaller without
 * moving where it sits.
 *
 * The pivot defaults to the middle of the selection's own range rather than to
 * the middle of the lane, because "make this gesture more dramatic" means more
 * dramatic about itself. A factor of zero flattens the selection onto the pivot,
 * which is a useful way to remove a wobble without deleting the points that hold
 * the shape either side of it.
 */
export const scaleAutomationPoints = defineCommand<{
  laneId: string;
  pointIds: string[];
  factor: number;
  pivot: number | null;
}>({
  type: 'automation.scalePoints',
  label: () => 'Scale automation',
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    const pointIds = idsParam(raw.pointIds);
    const factor = numberParam(raw.factor, 0, 8);
    if (!laneId || !pointIds || pointIds.length === 0 || factor === null) return null;
    const pivot = omitted(raw.pivot) ? null : numberParam(raw.pivot, 0, 1);
    if (raw.pivot !== undefined && raw.pivot !== null && pivot === null) return null;
    return { laneId, pointIds, factor, pivot };
  },
  coalesceKey: (p) => `autoScale:${p.laneId}:${p.pointIds.join(',')}`,
  apply: (project, p) => {
    const chosen = new Set(p.pointIds);
    return withLane(project, p.laneId, (lane) => {
      const selected = lane.points.filter((point) => chosen.has(point.id));
      if (selected.length === 0) return lane;

      let pivot = p.pivot;
      if (pivot === null) {
        let lowest = 1;
        let highest = 0;
        for (const point of selected) {
          lowest = Math.min(lowest, point.value);
          highest = Math.max(highest, point.value);
        }
        pivot = (lowest + highest) / 2;
      }

      const points = lane.points.map((point) =>
        chosen.has(point.id)
          ? { ...point, value: Math.min(1, Math.max(0, pivot + (point.value - pivot) * p.factor)) }
          : point,
      );
      return { ...lane, points };
    });
  },
});

/**
 * Removes the points a straight line through their neighbours would have put in
 * the same place anyway.
 *
 * Given a selection it thins only within it, so a recorded pass can be tidied
 * without touching the curve either side of it. The first and last points of the
 * run are always kept, so the curve still meets whatever it was joined to.
 */
export const thinAutomationPoints = defineCommand<{
  laneId: string;
  pointIds: string[] | null;
  tolerance: number;
}>({
  type: 'automation.thinPoints',
  label: () => 'Thin automation',
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    const tolerance = numberParam(raw.tolerance, 0, 1);
    if (!laneId || tolerance === null) return null;
    const pointIds = omitted(raw.pointIds) ? null : idsParam(raw.pointIds);
    if (raw.pointIds !== undefined && raw.pointIds !== null && pointIds === null) return null;
    return { laneId, pointIds, tolerance };
  },
  apply: (project, p) => {
    return withLane(project, p.laneId, (lane) => {
      const chosen = p.pointIds === null || p.pointIds.length === 0 ? null : new Set(p.pointIds);
      const run = chosen === null ? lane.points : lane.points.filter((point) => chosen.has(point.id));
      if (run.length <= 2) return lane;

      const keep = new Set(thinPoints(run, p.tolerance).map((point) => point.id));
      const points = lane.points.filter((point) => (chosen && !chosen.has(point.id)) || keep.has(point.id));
      if (points.length === lane.points.length) return lane;
      return { ...lane, points };
    });
  },
});

export const deleteAutomationPoints = defineCommand<{ laneId: string; pointIds: string[] }>({
  type: 'automation.deletePoints',
  label: (p) => (p.pointIds.length === 1 ? 'Delete automation point' : `Delete ${p.pointIds.length} automation points`),
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    const pointIds = idsParam(raw.pointIds);
    return laneId && pointIds && pointIds.length > 0 ? { laneId, pointIds } : null;
  },
  apply: (project, p) =>
    withLane(project, p.laneId, (lane) => {
      const doomed = new Set(p.pointIds);
      const points = lane.points.filter((point) => !doomed.has(point.id));
      // A lane with no points does nothing; keeping the last one preserves the
      // level the user set instead of silently handing control back.
      if (points.length === lane.points.length) return lane;
      return { ...lane, points: points.length > 0 ? points : lane.points.slice(0, 1) };
    }),
});

export const setAutomationCurve = defineCommand<{ laneId: string; pointIds: string[]; curve: AutomationCurve }>({
  type: 'automation.setCurve',
  label: (p) => `Set automation curve to ${p.curve}`,
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    const pointIds = idsParam(raw.pointIds);
    const curve = enumParam(raw.curve, CURVES);
    return laneId && pointIds && pointIds.length > 0 && curve ? { laneId, pointIds, curve } : null;
  },
  apply: (project, p) =>
    withLane(project, p.laneId, (lane) => {
      const targets = new Set(p.pointIds);
      return {
        ...lane,
        points: lane.points.map((point) => (targets.has(point.id) ? { ...point, curve: p.curve } : point)),
      };
    }),
});

export const clearAutomationLane = defineCommand<{ laneId: string }>({
  type: 'automation.clearLane',
  label: () => 'Clear automation lane',
  parse: (raw) => {
    const laneId = idParam(raw.laneId);
    return laneId ? { laneId } : null;
  },
  apply: (project, p) =>
    withLane(project, p.laneId, (lane) =>
      lane.points.length <= 1 ? lane : { ...lane, points: [{ ...lane.points[0], tick: 0 }] },
    ),
});
